import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2,
  Send,
  AlertCircle,
  Bot,
  CheckCircle2,
  CircleDashed,
  HelpCircle,
  Lightbulb,
  Sparkles,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

const PRIORITY_STYLES = {
  high: "border-red-500/40 text-red-300",
  normal: "border-amber-500/40 text-amber-300",
  low: "border-zinc-700 text-zinc-400",
};

const STATUS_STYLES = {
  open: "border-amber-500/40 text-amber-200",
  answered: "border-emerald-500/40 text-emerald-200",
  dismissed: "border-zinc-700 text-zinc-500",
};

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  } catch (_) {
    return "";
  }
}

function AnswerForm({ request, onAnswered }) {
  const [answer, setAnswer] = useState("");
  const [ingest, setIngest] = useState(true);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!answer.trim()) return;
    setBusy(true);
    try {
      await crmApi.answerKnowledgeRequest(request.agent_slug, request.id, {
        answer: answer.trim(),
        ingest_into_library: ingest,
      });
      toast.success(
        ingest
          ? "Answered — knowledge ingested into the library for all agents"
          : "Answered (not ingested)",
      );
      setAnswer("");
      onAnswered?.();
    } catch (err) {
      toast.error(err.message || "Failed to answer");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2 pt-2 border-t border-zinc-800">
      <Textarea
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        placeholder={`Owner answer for ${request.agent_slug}…`}
        className="min-h-[100px] bg-black border-zinc-800 text-white text-sm"
        data-testid={`kr-answer-${request.id}`}
      />
      <div className="flex items-center justify-between gap-3">
        <label className="text-[11px] text-zinc-400 inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={ingest}
            onChange={(e) => setIngest(e.target.checked)}
            className="accent-amber-500"
            data-testid={`kr-ingest-${request.id}`}
          />
          Auto-ingest answer into Owner Q&amp;A binder (every agent inherits it)
        </label>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                await crmApi.dismissKnowledgeRequest(request.agent_slug, request.id);
                toast.success("Dismissed");
                onAnswered?.();
              } catch (err) {
                toast.error(err.message || "Failed to dismiss");
              }
            }}
            className="border-zinc-800 text-zinc-400 hover:bg-zinc-900"
            data-testid={`kr-dismiss-${request.id}`}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            Dismiss
          </Button>
          <Button
            size="sm"
            onClick={submit}
            disabled={busy || !answer.trim()}
            className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
            data-testid={`kr-submit-${request.id}`}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
            Answer &amp; ingest
          </Button>
        </div>
      </div>
    </div>
  );
}

function KnowledgeRequestsTab({ agent }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open"); // "open" | "answered" | "all"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const res = await crmApi.listKnowledgeRequests(agent.slug, params);
      setItems(res?.requests || []);
    } catch (err) {
      toast.error(err.message || "Failed to load knowledge requests");
    } finally {
      setLoading(false);
    }
  }, [agent.slug, filter]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-3" data-testid={`kr-tab-${agent.slug}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[12.5px] text-zinc-400">
          <Lightbulb className="inline h-3.5 w-3.5 mr-1 text-amber-300" />
          {agent.display_name} auto-files a request whenever they hit a gap.
          Your answers auto-ingest into the live library so all 11 agents inherit them.
        </div>
        <div className="flex gap-1 text-[11px]">
          {["open", "answered", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-2.5 py-1 rounded uppercase tracking-[0.14em] border ${
                filter === f
                  ? "bg-amber-500/15 border-amber-500/40 text-amber-200"
                  : "border-zinc-800 text-zinc-500 hover:text-zinc-300"
              }`}
              data-testid={`kr-filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-[12px] flex items-center gap-2 py-6 justify-center">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="py-8 text-center text-sm text-zinc-500">
            <CircleDashed className="h-6 w-6 text-zinc-700 mx-auto mb-2" />
            No {filter === "all" ? "" : filter} knowledge requests for {agent.display_name}.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <Card
              key={r.id}
              className="bg-zinc-950 border-zinc-800"
              data-testid={`kr-row-${r.id}`}
            >
              <CardContent className="p-3.5 space-y-2.5">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge
                        variant="outline"
                        className={STATUS_STYLES[r.status] || "border-zinc-700"}
                      >
                        {r.status}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={PRIORITY_STYLES[r.priority] || "border-zinc-700"}
                      >
                        {r.priority}
                      </Badge>
                      <span className="text-[11px] text-zinc-500">
                        {timeAgo(r.created_at)}
                      </span>
                      {r.ingested_chunk_id ? (
                        <Badge
                          variant="outline"
                          className="border-violet-500/40 text-violet-200 text-[10px]"
                          title="Owner answer was ingested into the live library"
                        >
                          <Sparkles className="h-3 w-3 mr-1" /> ingested
                        </Badge>
                      ) : null}
                    </div>
                    <div className="text-white font-medium mt-1.5">
                      {r.topic}
                    </div>
                  </div>
                </div>

                <div className="text-[13px] text-zinc-200 whitespace-pre-wrap">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 block mb-1">
                    Question
                  </span>
                  {r.question}
                </div>

                {r.why ? (
                  <div className="text-[12px] text-zinc-400">
                    <span className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 block mb-1">
                      Why
                    </span>
                    {r.why}
                  </div>
                ) : null}

                {r.context_excerpt ? (
                  <details className="text-[12px] text-zinc-400">
                    <summary className="cursor-pointer text-[11px] uppercase tracking-[0.14em] text-zinc-500 hover:text-zinc-300">
                      Triggering message
                    </summary>
                    <div className="mt-1.5 italic">“{r.context_excerpt}”</div>
                  </details>
                ) : null}

                {r.status === "answered" && r.answer ? (
                  <div className="text-[13px] text-emerald-100 bg-emerald-500/5 border border-emerald-500/20 rounded p-2.5">
                    <span className="text-[11px] uppercase tracking-[0.14em] text-emerald-300 block mb-1">
                      Owner answer
                    </span>
                    <div className="whitespace-pre-wrap">{r.answer}</div>
                    <div className="text-[10.5px] text-zinc-500 mt-1.5">
                      Answered by {r.answered_by || "owner"} · {timeAgo(r.answered_at)}
                    </div>
                  </div>
                ) : null}

                {r.status === "open" ? (
                  <AnswerForm request={r} onAnswered={load} />
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function AgentChatPanel({ agent }) {
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const taRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const past = await crmApi.agentHistory(agent.slug, 10);
        const initial = (past || []).slice().reverse().flatMap((h) => [
          { role: "user", text: h.user_message, ts: h.created_at },
          {
            role: "agent",
            text: h.agent_reply,
            citations: h.citations,
            ts: h.created_at,
            latency_ms: h.latency_ms,
            knowledge_request_ids: h.knowledge_request_ids,
          },
        ]);
        setHistory(initial);
      } catch {
        /* non-blocking */
      }
    })();
  }, [agent.slug]);

  const send = async () => {
    const text = message.trim();
    if (!text || busy) return;
    setBusy(true);
    setHistory((h) => [...h, { role: "user", text, ts: new Date().toISOString() }]);
    setMessage("");
    try {
      const res = await crmApi.chatAgent(agent.slug, { message: text });
      setHistory((h) => [
        ...h,
        {
          role: "agent",
          text: res.reply || "",
          citations: res.citations || [],
          latency_ms: res.latency_ms,
          error: res.error,
          knowledge_request_ids: res.knowledge_requests_flagged || [],
          ts: new Date().toISOString(),
        },
      ]);
      if (res.error) toast.error(`Agent error: ${res.error}`);
      else if ((res.knowledge_requests_flagged || []).length)
        toast.message(
          `${agent.display_name} flagged ${res.knowledge_requests_flagged.length} knowledge gap${
            res.knowledge_requests_flagged.length === 1 ? "" : "s"
          }`,
          { description: "Open the Knowledge Requests tab to answer." },
        );
    } catch (exc) {
      toast.error(exc.message || "Chat failed");
      setHistory((h) => [...h, { role: "agent", text: `⚠ ${exc.message}`, error: exc.message, ts: new Date().toISOString() }]);
    } finally {
      setBusy(false);
      taRef.current?.focus();
    }
  };

  return (
    <div className="flex flex-col gap-3 h-[640px]" data-testid={`agent-chat-${agent.slug}`}>
      <ScrollArea className="flex-1 rounded-md border border-zinc-800 bg-black/60 p-3">
        {history.length === 0 && (
          <div className="text-sm text-zinc-500">
            Start a conversation. {agent.display_name} reads the active Sales Knowledge Library
            binders before every reply.
          </div>
        )}
        <div className="space-y-3">
          {history.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div
                className={
                  m.role === "user"
                    ? "bg-amber-500/15 border border-amber-500/30 text-amber-50 rounded-lg px-3 py-2 max-w-[80%]"
                    : "bg-zinc-900 border border-zinc-800 text-white rounded-lg px-3 py-2 max-w-[80%]"
                }
              >
                <div className="whitespace-pre-wrap text-sm" data-testid={`msg-${m.role}-${i}`}>
                  {m.text}
                </div>
                {m.role === "agent" && (m.citations?.length || m.latency_ms || (m.knowledge_request_ids?.length)) ? (
                  <div className="text-[10px] text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                    {m.latency_ms ? <span>{m.latency_ms}ms</span> : null}
                    {m.citations?.length ? <span>· {m.citations.length} library citations</span> : null}
                    {m.knowledge_request_ids?.length ? (
                      <span className="text-amber-300">
                        · {m.knowledge_request_ids.length} knowledge gap{m.knowledge_request_ids.length === 1 ? "" : "s"} flagged
                      </span>
                    ) : null}
                    {m.error ? <span className="text-rose-400">· error</span> : null}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
      <div className="flex items-end gap-2">
        <Textarea
          ref={taRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={`Ask ${agent.display_name} anything… (Cmd/Ctrl+Enter to send)`}
          rows={2}
          data-testid="agent-chat-input"
          className="bg-black border-zinc-800 text-white"
        />
        <Button
          onClick={send}
          disabled={busy || !message.trim()}
          className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
          data-testid="agent-chat-send"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

function AgentDetail({ agent, onBack, openKrCount }) {
  return (
    <Card className="bg-zinc-900/40 border-zinc-800" data-testid={`agent-detail-${agent.slug}`}>
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
        <div>
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Bot className="h-4 w-4 text-amber-300" /> {agent.display_name}
            <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px]">
              {agent.role}
            </Badge>
          </CardTitle>
          <p className="text-xs text-zinc-400 mt-1 max-w-prose">{agent.duties}</p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onBack}
          className="text-zinc-400"
          data-testid="agent-chat-back"
        >
          ← All agents
        </Button>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="chat" className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="chat" className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200" data-testid="agent-tab-chat">
              Chat
            </TabsTrigger>
            <TabsTrigger
              value="knowledge"
              className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200"
              data-testid="agent-tab-knowledge"
            >
              <HelpCircle className="h-3.5 w-3.5 mr-1.5" />
              Knowledge Requests
              {openKrCount > 0 ? (
                <Badge
                  variant="outline"
                  className="ml-1.5 border-amber-500/40 text-amber-300 text-[10px] px-1.5 py-0"
                  data-testid="agent-tab-knowledge-count"
                >
                  {openKrCount}
                </Badge>
              ) : null}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="chat" className="mt-3">
            <AgentChatPanel agent={agent} />
          </TabsContent>
          <TabsContent value="knowledge" className="mt-3">
            <KnowledgeRequestsTab agent={agent} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

export default function AgentsView() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [krSummary, setKrSummary] = useState({ by_agent_open: {}, by_status: {}, total: 0 });

  const loadSummary = useCallback(async () => {
    try {
      const sum = await crmApi.knowledgeRequestsSummary();
      setKrSummary(sum || { by_agent_open: {}, by_status: {}, total: 0 });
    } catch (_) {
      /* non-blocking */
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const list = await crmApi.listAgents();
        setAgents(list || []);
      } catch (exc) {
        toast.error(exc.message || "Failed to load agents");
      } finally {
        setLoading(false);
      }
    })();
    loadSummary();
  }, [loadSummary]);

  // Refresh summary whenever we leave a detail view (so card counts stay live).
  useEffect(() => {
    if (!selected) loadSummary();
  }, [selected, loadSummary]);

  if (loading) {
    return (
      <div className="text-sm text-zinc-400 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading agents…
      </div>
    );
  }

  if (selected) {
    return (
      <AgentDetail
        agent={selected}
        onBack={() => setSelected(null)}
        openKrCount={krSummary.by_agent_open?.[selected.slug] || 0}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="owner-agents">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Agent Swarm</h1>
          <p className="text-sm text-zinc-400 mt-1">
            All 11 agents are live. Tap any agent to test-chat or to review the knowledge gaps
            they've flagged. Riley and Dana can also read Owner Vault content.
          </p>
        </div>
        {krSummary.total > 0 ? (
          <div className="text-[11px] text-zinc-400 flex items-center gap-2" data-testid="kr-global-summary">
            <Lightbulb className="h-3.5 w-3.5 text-amber-300" />
            <span>
              {krSummary.by_status?.open || 0} open · {krSummary.by_status?.answered || 0} answered · {krSummary.by_status?.dismissed || 0} dismissed
            </span>
          </div>
        ) : null}
      </div>

      {agents.length === 0 ? (
        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardContent className="py-10 text-center text-sm text-zinc-400">
            <AlertCircle className="h-6 w-6 text-amber-300 mx-auto mb-2" />
            No agents seeded. Restart the backend to run the Phase A seed.
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {agents.map((a) => {
            const openCount = krSummary.by_agent_open?.[a.slug] || 0;
            return (
              <button
                key={a.slug}
                onClick={() => setSelected(a)}
                className="text-left rounded-lg border border-zinc-800 bg-zinc-900/40 hover:border-amber-500/40 hover:bg-zinc-900/70 transition-colors p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60 relative"
                data-testid={`agent-card-${a.slug}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium text-white">{a.display_name}</div>
                  <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px]">
                    {a.role}
                  </Badge>
                </div>
                <div className="text-xs text-zinc-400 mt-2 line-clamp-3">{a.duties}</div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-[11px] text-amber-300">Open agent →</div>
                  {openCount > 0 ? (
                    <Badge
                      variant="outline"
                      className="border-amber-500/40 text-amber-300 text-[10px]"
                      data-testid={`agent-card-kr-${a.slug}`}
                    >
                      <Lightbulb className="h-3 w-3 mr-1" />
                      {openCount} gap{openCount === 1 ? "" : "s"}
                    </Badge>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
